import * as React from "react";
import type { Ordering } from "@/types/base";
import { useGetSnapshotEventsQuery } from "@/modules/snapshots/businessReports.api";
import { canEditOutcomeResults } from "@/modules/snapshots/businessReports.types";
import { BusinessReportPromptEditModal } from "@/modules/snapshots/components/BusinessReportPromptEditModal";
import { EMPTY_PROMPT } from "@/modules/snapshots/components/BusinessReportPromptEditModal.constants";
import type { SortOption } from "@/components/sortDropdown";
import { Loader } from "@/components/ui/loader";
import { Button } from "@/components/ui/button";
import { useNavigate, useSearchParams } from "react-router";
import { CurrentPdfProvider } from "@/contexts/setPdfContext";
import { SnapshotGridTable } from "@/modules/snapshots/grid/table";
import { useGetActiveDirectoryUsersQuery } from "@/api/users-admin";
import { buildSnapshotGridPath } from "@/modules/snapshots/routes";
import { SnapshotGridBreadcrumbs } from "@/modules/snapshots/grid/breadcrumbs";
import { ReviewRunConfirmDialog } from "@/modules/snapshots/grid/reviewRunDialog";
import TopBarProgress from "react-topbar-progress-indicator";
import { SnapshotDateFilterModal } from "@/modules/snapshots/grid/dateFilterModal";
import { SnapshotGridPortal } from "@/modules/snapshots/grid/portal";
import { useToggle } from "react-use";
import clsx from "clsx";
import { useFavoriteCollection } from "@/modules/snapshots/snapshotCollections/hooks/useFavoriteCollection";
import { SnapshotCollectionShareModal } from "@/modules/snapshots/modals/snapshotCollectionShareModal";
import { useSnapshotPermission } from "@/modules/snapshots/hooks/useSnapshotPermission";
import type { SnapshotCollection, Subcollection } from "@/modules/snapshots/snapshotCollection.types";
import type { ContractFamilyProjectionGridQueryResponse } from "@/modules/snapshots/businessReports.api";
import { SnapshotGridTabs } from "@/modules/snapshots/grid/components/SnapshotGridTabs";
import { SaveSnapshotViewDialog } from "@/modules/snapshots/grid/SaveSnapshotViewDialog";
import { DEFAULT_ORDERING, PAGE_SIZE } from "@/modules/snapshots/grid/constants";
import type { CollectionProps, SnapshotGridCoreProps } from "@/modules/snapshots/grid/types";
import { PipelineSnapshotGridCore } from "@/modules/snapshots/grid/components/PipelineSnapshotGridCore";
import { SnapshotGridHeader } from "@/modules/snapshots/grid/components/SnapshotGridHeader";
import { SubcollectionControl } from "@/modules/snapshots/grid/components/SubcollectionControl";
import { SnapshotGridToolbar } from "@/modules/snapshots/grid/components/SnapshotGridToolbar";
import { SnapshotQaSplitView } from "@/modules/snapshots/grid/components/SnapshotQaSplitView";
import { ProjectionReadinessBanner } from "@/modules/snapshots/grid/components/ProjectionReadinessBanner";
import { SupplierSummaryCollectionNotice } from "@/modules/snapshots/grid/supplierSummaryCollectionDialog";
import { FamilySourceDocumentsDialog } from "@/modules/snapshots/grid/components/FamilySourceDocumentsDialog";
import { DeleteSelectedSnapshotsDialog } from "@/modules/snapshots/grid/components/DeleteSelectedSnapshotsDialog";
import { useSnapshotGridPageCursor, useSnapshotGridRows } from "@/modules/snapshots/grid/hooks/useSnapshotGridRows";
import { useSnapshotGridColumnLayout } from "@/modules/snapshots/grid/hooks/useSnapshotGridColumnLayout";
import { useSnapshotGridColumnFilters } from "@/modules/snapshots/grid/hooks/useSnapshotGridColumnFilters";
import { useSnapshotGridColumnWidths } from "@/modules/snapshots/grid/hooks/useSnapshotGridColumnWidths";
import { useSnapshotGridDateFilter } from "@/modules/snapshots/grid/hooks/useSnapshotGridDateFilter";
import { useInitialViewConfiguration } from "@/modules/snapshots/grid/hooks/useInitialViewConfiguration";
import { useRetainedSnapshotCollection } from "@/modules/snapshots/grid/hooks/useRetainedSnapshotCollection";
import { useAiColumnGeneration } from "@/modules/snapshots/grid/hooks/useAiColumnGeneration";
import { useColumnReviewRuns } from "@/modules/snapshots/grid/hooks/useColumnReviewRuns";
import { useSnapshotGridFieldMeta } from "@/modules/snapshots/grid/hooks/useSnapshotGridFieldMeta";
import { useSnapshotGridDashboard } from "@/modules/snapshots/grid/hooks/useSnapshotGridDashboard";
import { useSnapshotGridDetailSidebar } from "@/modules/snapshots/grid/hooks/useSnapshotGridDetailSidebar";
import { useDeleteSelectedSnapshots } from "@/modules/snapshots/grid/hooks/useDeleteSelectedSnapshots";
import { useLoadMoreObserver } from "@/modules/snapshots/grid/hooks/useLoadMoreObserver";
import { useActiveSubcollection } from "@/modules/snapshots/grid/hooks/useActiveSubcollection";

export type { CollectionProps, PipelinePreviewProps, SnapshotGridCoreProps } from "@/modules/snapshots/grid/types";

const PivotBuilderDialog = React.lazy(() => import("@/modules/snapshots/grid/pivots/PivotBuilderDialog"));
const PivotDashboard = React.lazy(() => import("@/modules/snapshots/grid/pivots/PivotDashboard"));

export const SnapshotGridCore: React.FC<SnapshotGridCoreProps> = props =>
  props.mode === "pipeline-preview" ? (
    <CurrentPdfProvider>
      <PipelineSnapshotGridCore {...props} />
    </CurrentPdfProvider>
  ) : (
    <SnapshotGridCollectionCore {...props} />
  );

const SnapshotGridCollectionCore: React.FC<CollectionProps> = ({
  snapshotCollectionId,
  templateId,
  mode,
  capabilities,
  refreshSignal,
  onQueryChange,
  snapshotView,
  initialConfiguration,
}) => {
  const [query, setQuery] = React.useState("");
  const [ordering, setOrdering] = React.useState<Ordering>(DEFAULT_ORDERING);
  const [selectedSnapshots, setSelectedSnapshots] = React.useState<string[]>([]);
  const [viewColumnOrder, setViewColumnOrder] = React.useState<string[]>(initialConfiguration?.column_order ?? []);
  const [showSaveViewDialog, setShowSaveViewDialog] = React.useState(false);
  const [columnAnalysisField, setColumnAnalysisField] = React.useState<string | null>(null);
  const [showQaPanel, toggleQaPanel] = useToggle(false);
  const [qaExpanded, toggleQaExpanded] = useToggle(false);
  const [expandedFamilyId, setExpandedFamilyId] = React.useState<string | null>(null);

  const navigate = useNavigate();
  const collectionId = snapshotCollectionId;
  const [isShareOpen, toggleIsShareOpen] = useToggle(false);
  const { isFavorited, handleToggleFavorite } = useFavoriteCollection(collectionId ?? "", { skip: mode === "view" });
  const [searchParams, setSearchParams] = useSearchParams();
  const isContractFamilyProjection = mode === "contract-family-projection";
  const isCompactPreview = !capabilities.chrome;
  const subcollectionId = searchParams.get("subcollection_id") ?? undefined;
  const contractFamilyId = searchParams.get("contractFamilyId") ?? undefined;
  const supplierContractFolderId = searchParams.get("supplierContractFolderId") ?? undefined;
  const owners = useGetActiveDirectoryUsersQuery(undefined, {
    skip: contractFamilyId === undefined && supplierContractFolderId === undefined,
  });
  const ownerNamesById = React.useMemo(
    () => new Map((owners.data ?? []).map(owner => [owner.id, `${owner.name} ${owner.surname}`.trim()])),
    [owners.data],
  );

  const clearSelection = React.useCallback(() => setSelectedSnapshots([]), []);
  const pageCursor = useSnapshotGridPageCursor(clearSelection);
  const { resetGridPageForViewChange } = pageCursor;
  const layout = useSnapshotGridColumnLayout(collectionId);
  const { hiddenColumns, columnOrder, noteColumnOrder, removeNoteColumn } = layout;
  const { columnFilters, setColumnFilters, handleColumnFilterChange, handleClearAllColumnFilters } =
    useSnapshotGridColumnFilters(collectionId, resetGridPageForViewChange);
  const { columnWidths, handleColumnResize } = useSnapshotGridColumnWidths(collectionId);
  const dateFilterState = useSnapshotGridDateFilter(resetGridPageForViewChange);
  const { dateFilter, setDateFilter } = dateFilterState;

  const handleNoteColumnDeleted = React.useCallback(
    (columnName: string) => {
      handleColumnFilterChange(columnName, undefined);
      removeNoteColumn(columnName);
    },
    [handleColumnFilterChange, removeNoteColumn],
  );

  const { snapshotCollection, businessReport, businessReportId, siblingCollections } =
    useRetainedSnapshotCollection(collectionId);
  const isOwner = useSnapshotPermission(snapshotCollection ?? ({ permissions: [] } as unknown as SnapshotCollection));
  const { activeSubcollection, activeSubcollectionName, activeSubcollectionCount, projectionReadiness } =
    useActiveSubcollection({
      collectionId,
      subcollectionId,
      isContractFamilyProjection,
      collectionSnapshotCount: snapshotCollection?.snapshots,
    });

  const initialConfigurationApplied = React.useRef(false);
  const {
    gridQueryPayload,
    effectiveViewConfiguration,
    isViewDirty,
    gridMetadata,
    effectiveGridMetadata,
    gridCapabilities,
    fetchRowByResult,
    refetch,
    isError,
    isFetching,
    loadedGridRows,
    hasRenderableGrid,
    totalGridRows,
    isGridResultStale,
    isInitialGridLoading,
    isLoadingMoreGridRows,
    hasNextGridPage,
    requestNextGridPage,
  } = useSnapshotGridRows({
    cursor: pageCursor,
    mode,
    collectionId,
    snapshotViewId: snapshotView?.id,
    capabilities,
    refreshSignal,
    onQueryChange,
    isContractFamilyProjection,
    subcollectionId,
    contractFamilyId,
    supplierContractFolderId,
    query,
    ordering,
    columnFilters,
    dateFilter,
    initialConfiguration,
    initialConfigurationAppliedRef: initialConfigurationApplied,
    hiddenColumns,
    columnOrder,
    noteColumnOrder,
    dateRangeTouched: dateFilterState.dateRangeTouched,
    stableColumnOrder: mode === "view" ? viewColumnOrder : undefined,
  });

  useInitialViewConfiguration({
    initialConfiguration,
    fields: gridMetadata?.fields,
    appliedRef: initialConfigurationApplied,
    setHiddenColumns: layout.setHiddenColumns,
    setColumnOrder: layout.setColumnOrder,
    setNoteColumnOrder: layout.setNoteColumnOrder,
    setColumnFilters,
    setOrdering,
    setDateFilter,
  });

  React.useEffect(() => {
    if (!isViewDirty) return;
    const preventUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", preventUnload);
    return () => window.removeEventListener("beforeunload", preventUnload);
  }, [isViewDirty]);

  const aiColumn = useAiColumnGeneration({
    collectionId,
    businessReportId,
    refetch,
  });

  const handleOrderingChange = React.useCallback(
    (nextOrdering: Ordering) => {
      if (ordering.order_by === nextOrdering.order_by && ordering.descending === nextOrdering.descending) {
        return;
      }

      React.startTransition(() => {
        resetGridPageForViewChange();
        setOrdering(nextOrdering);
      });
    },
    [ordering.descending, ordering.order_by, resetGridPageForViewChange],
  );

  const onSortChange = React.useCallback(
    ({ field, direction }: SortOption) => {
      handleOrderingChange({ order_by: field, descending: direction === "desc" });
    },
    [handleOrderingChange],
  );

  const handleQueryChange = React.useCallback(
    (nextQuery: string) => {
      resetGridPageForViewChange();
      setQuery(nextQuery);
    },
    [resetGridPageForViewChange],
  );

  const snapshotCollectionOutcomes = React.useMemo(
    () => loadedGridRows.flatMap(row => (row.sourceOutcome ? [row.sourceOutcome] : [])),
    [loadedGridRows],
  );
  const backendFields = React.useMemo(() => effectiveGridMetadata?.fields ?? [], [effectiveGridMetadata?.fields]);
  const { availableDateFields, hasDatePrompts, allColumnHeaders, sortFields, columnTypes } =
    useSnapshotGridFieldMeta(backendFields);

  const review = useColumnReviewRuns({
    collectionId,
    fields: backendFields,
    businessReportPrompts: businessReport?.prompts,
    refetch,
  });

  const snapshotCompleted = React.useMemo(
    () => snapshotCollectionOutcomes.filter(outcome => outcome.status === "COMPLETED"),
    [snapshotCollectionOutcomes],
  );
  const snapshotRows = React.useMemo(
    () => loadedGridRows.filter(row => row.sourceOutcome?.status === "COMPLETED"),
    [loadedGridRows],
  );
  const canReviewColumns = gridCapabilities.reviewTriage && !snapshotCollection?.is_family_collection;

  const handleSelectAll = (checked: boolean) => {
    setSelectedSnapshots(checked ? snapshotCompleted.map(snapshot => snapshot.id) : []);
  };

  const dashboard = useSnapshotGridDashboard({
    collectionId,
    subcollectionId,
    snapshotCompleted,
    isGridResultStale,
    totalGridRows,
  });

  const detail = useSnapshotGridDetailSidebar({
    searchParams,
    setSearchParams,
    collectionId,
    subcollectionId,
    snapshotCompleted,
    fetchRowByResult,
  });
  const { setSidebarOutcomeId, setSidebarResultId, closeSidebar } = detail;

  const bulkDelete = useDeleteSelectedSnapshots({
    subcollectionId,
    isContractFamilyProjection,
    selectedSnapshots,
    setSelectedSnapshots,
    refetch,
  });
  const { setShowDeleteDialog } = bulkDelete;

  const handleSubcollectionCreated = React.useCallback(
    (subcollection: Subcollection) => {
      if (!businessReportId || !collectionId) return;

      setSelectedSnapshots([]);
      setQuery("");
      setDateFilter({ startDate: null, endDate: null, selectedField: null });
      handleClearAllColumnFilters();
      setSidebarOutcomeId(null);
      setSidebarResultId(null);
      navigate(
        buildSnapshotGridPath(businessReportId, collectionId, {
          subcollectionId: subcollection.id,
          contractFamilyProjection: isContractFamilyProjection,
          supplierContractFolderId,
        }),
      );
    },
    [
      businessReportId,
      collectionId,
      handleClearAllColumnFilters,
      isContractFamilyProjection,
      navigate,
      setDateFilter,
      setSidebarOutcomeId,
      setSidebarResultId,
      supplierContractFolderId,
    ],
  );

  React.useEffect(() => {
    if (!isGridResultStale) return;
    setShowDeleteDialog(false);
    setColumnAnalysisField(null);
    closeSidebar();
  }, [closeSidebar, isGridResultStale, setShowDeleteDialog]);

  const activeColumnFilterCount = Object.keys(columnFilters).length;

  const projectionRows = React.useMemo<ContractFamilyProjectionGridQueryResponse["projection_rows"]>(
    () => (isContractFamilyProjection ? (effectiveGridMetadata?.projectionRows ?? {}) : {}),
    [effectiveGridMetadata, isContractFamilyProjection],
  );

  const handleOutcomeClick = React.useCallback(
    (outcomeId: string, _detectedFields: string[]) => {
      const projectionRow = projectionRows[outcomeId];
      if (projectionRow?.row_kind === "family_summary" && projectionRow.family_id) {
        setExpandedFamilyId(projectionRow.family_id);
        return;
      }
      navigate(`/reports/outcome/${outcomeId}`);
    },
    [navigate, projectionRows],
  );

  const handleOpenQa = React.useCallback(
    (outcomeId: string) => {
      const outcome = snapshotCompleted.find(o => o.id === outcomeId);
      if (!outcome?.fs_entry || !businessReport || !snapshotCollection) {
        return;
      }
      navigate(`/chat/${outcome.fs_entry.id}`, {
        state: {
          fromSnapshot: `/snapshots/grid/${businessReport.id}/${snapshotCollection.id}?outcomeId=${outcomeId}`,
        },
      });
    },
    [navigate, snapshotCompleted, businessReport, snapshotCollection],
  );

  const handleCloseQaPanel = React.useCallback(() => {
    toggleQaPanel(false);
    toggleQaExpanded(false);
  }, [toggleQaPanel, toggleQaExpanded]);

  const filteredOutcomeIds = React.useMemo(
    () =>
      !isGridResultStale && Object.keys(gridQueryPayload.column_filters).length > 0
        ? effectiveGridMetadata?.filteredOutcomeIds
        : undefined,
    [effectiveGridMetadata?.filteredOutcomeIds, gridQueryPayload.column_filters, isGridResultStale],
  );

  const { loadMoreRef, tableScrollRef } = useLoadMoreObserver(
    hasNextGridPage && !isFetching && dashboard.activeTab === "table",
    requestNextGridPage,
  );

  const businessReportOutcomeIds = React.useMemo(
    () => snapshotCompleted.map(outcome => outcome.id),
    [snapshotCompleted],
  );

  const { data: events = [] } = useGetSnapshotEventsQuery(
    {
      businessReportOutcomeId: businessReportOutcomeIds,
    },
    {
      skip: !businessReportOutcomeIds.length,
    },
  );

  const businessReportOutcomeResultWithEvent = React.useMemo(
    () =>
      new Set(
        events.flatMap(row => (row.business_report_outcome_result_id ? [row.business_report_outcome_result_id] : [])),
      ),
    [events],
  );

  if (isError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2">
        <p className="text-red-600">Error loading snapshot collection outcomes</p>
        <button
          className="rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
          onClick={refetch}
        >
          Try Again
        </button>
      </div>
    );
  }

  if (isInitialGridLoading || !hasRenderableGrid || !snapshotCollection || !businessReport) {
    return <Loader />;
  }

  const subcollectionControl = gridCapabilities.subcollections ? (
    <SubcollectionControl
      snapshotCollectionId={snapshotCollection.id}
      subcollectionId={subcollectionId}
      activeSubcollectionName={activeSubcollectionName}
      activeSubcollectionCount={activeSubcollectionCount}
      isOwner={isOwner}
      contractFamilyProjection={isContractFamilyProjection}
    />
  ) : null;

  return (
    <>
      <CurrentPdfProvider>
        {isFetching && <TopBarProgress />}
        <div
          className={clsx(
            "flex flex-col overflow-hidden",
            isCompactPreview ? "h-full min-h-0" : "h-[calc(100vh-4rem)]",
          )}
        >
          <SnapshotGridHeader
            hidden={isCompactPreview}
            mode={mode}
            snapshotView={snapshotView}
            businessReport={businessReport}
            snapshotCollection={snapshotCollection}
            siblingCollections={siblingCollections}
            collectionId={collectionId}
            isContractFamilyProjection={isContractFamilyProjection}
            subcollectionControl={subcollectionControl}
            subcollectionId={subcollectionId}
            subcollectionName={activeSubcollection?.name}
            gridCapabilities={gridCapabilities}
            isFavorited={isFavorited}
            onToggleFavorite={handleToggleFavorite}
            onShare={toggleIsShareOpen}
            onSaveView={() => setShowSaveViewDialog(true)}
            filteredOutcomeIds={filteredOutcomeIds}
            isGridResultStale={isGridResultStale}
            viewConfiguration={effectiveViewConfiguration}
          />

          <div className={clsx("mb-5", isCompactPreview && "hidden")}>
            <SnapshotGridBreadcrumbs
              mode={mode}
              snapshotCollection={snapshotCollection}
              businessReport={businessReport}
              snapshotView={snapshotView}
            />
          </div>

          {!isCompactPreview && !isContractFamilyProjection && (
            <SupplierSummaryCollectionNotice snapshotCollection={snapshotCollection} />
          )}

          {supplierContractFolderId && (
            <div className="mb-3 flex items-center gap-3 rounded-md border border-coral/30 bg-orange-50 px-4 py-2 text-sm text-gray-800">
              <span>Contract Family: {snapshotCompleted[0]?.name ?? "No matching summary"}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.delete("supplierContractFolderId");
                  setSearchParams(next);
                }}
              >
                Clear family filter
              </Button>
            </div>
          )}

          {isContractFamilyProjection && projectionReadiness && (
            <ProjectionReadinessBanner readiness={projectionReadiness} />
          )}

          <SnapshotGridTabs
            activeTab={dashboard.activeTab}
            onActiveTabChange={dashboard.setActiveTab}
            showDashboard={gridCapabilities.dashboard}
            savedPivotCount={dashboard.savedPivotCount}
            hidden={isCompactPreview}
          />

          <div
            className={clsx(
              "flex min-h-0 flex-grow flex-col bg-white",
              !isCompactPreview && "mt-5 rounded-xl p-5 shadow-md",
            )}
          >
            {dashboard.activeTab === "table" ? (
              <div className="flex min-h-0 flex-grow flex-col">
                <div
                  className={clsx(
                    "flex min-h-0 flex-grow flex-col",
                    !isCompactPreview && "-mx-4 -my-2 sm:-mx-6 lg:-mx-8",
                  )}
                >
                  <div
                    className={clsx(
                      "flex min-h-0 flex-grow flex-col align-middle",
                      !isCompactPreview && "py-2 sm:px-6 lg:px-8",
                    )}
                  >
                    <SnapshotGridToolbar
                      hidden={isCompactPreview}
                      gridCapabilities={gridCapabilities}
                      isGridResultStale={isGridResultStale}
                      onToggleQaPanel={toggleQaPanel}
                      isPivotDataComplete={dashboard.isPivotDataComplete}
                      pivotUnavailableMessage={dashboard.pivotUnavailableMessage}
                      isFamilyCollection={Boolean(snapshotCollection.is_family_collection)}
                      onOpenPivotBuilder={() => dashboard.togglePivotBuilderOpen(true)}
                      selectedSnapshots={selectedSnapshots}
                      snapshotCollectionId={snapshotCollection.id}
                      subcollectionId={subcollectionId}
                      onSubcollectionCreated={handleSubcollectionCreated}
                      isContractFamilyProjection={isContractFamilyProjection}
                      onDeleteSelected={() => setShowDeleteDialog(true)}
                      query={query}
                      onQueryChange={handleQueryChange}
                      isFetching={isFetching}
                      activeColumnFilterCount={activeColumnFilterCount}
                      onClearAllColumnFilters={handleClearAllColumnFilters}
                      hasDatePrompts={hasDatePrompts}
                      dateFilter={dateFilter}
                      onOpenDateFilterModal={() => dateFilterState.setShowDateFilterModal(true)}
                      sortFields={sortFields}
                      currentOrdering={ordering}
                      onSortChange={onSortChange}
                      allColumnHeaders={allColumnHeaders}
                      hiddenColumns={hiddenColumns}
                      onHiddenColumnsChange={layout.handleHiddenColumnsChange}
                      isOwner={isOwner}
                      onAddColumnClick={aiColumn.toggleAddColumnOpen}
                    />
                    <SnapshotQaSplitView
                      showQaPanel={showQaPanel}
                      qaExpanded={qaExpanded}
                      onToggleExpand={toggleQaExpanded}
                      onClose={handleCloseQaPanel}
                      businessReportId={businessReport.id}
                      snapshotCollectionId={snapshotCollection.id}
                      subcollectionId={subcollectionId}
                      contractFamilyProjection={isContractFamilyProjection}
                      onCitationClick={detail.handleCitationClick}
                      onOutcomeClick={handleOutcomeClick}
                      onOpenQa={handleOpenQa}
                    >
                      {contractFamilyId ? (
                        <SnapshotGridTable
                          rows={snapshotRows}
                          fields={backendFields}
                          sourceCollectionId={snapshotCollectionId}
                          templateId={templateId}
                          currentOrdering={ordering}
                          setOrdering={handleOrderingChange}
                          selectedGridRowId={detail.sidebarOutcomeId}
                          onSelectedGridRowChange={detail.setSidebarOutcomeId}
                          ownerNamesById={ownerNamesById}
                          emptyMessage="No contract summary is available for this family."
                        />
                      ) : (
                        <SnapshotGridTable
                          rows={snapshotRows}
                          fields={backendFields}
                          setOrdering={handleOrderingChange}
                          businessReportOutcomeResultWithEvent={businessReportOutcomeResultWithEvent}
                          snapshotCollection={snapshotCollection}
                          currentOrdering={ordering}
                          selectedSnapshots={gridCapabilities.bulkSelection ? selectedSnapshots : []}
                          onSelectedSnapshotsChange={gridCapabilities.bulkSelection ? setSelectedSnapshots : undefined}
                          onSelectAll={gridCapabilities.bulkSelection ? handleSelectAll : undefined}
                          onColumnAnalysisClick={gridCapabilities.columnAnalysis ? setColumnAnalysisField : undefined}
                          onReviewColumnClick={canReviewColumns && isOwner ? review.handleReviewColumnClick : undefined}
                          columnReview={canReviewColumns ? review.columnReview : undefined}
                          hiddenColumns={hiddenColumns}
                          columnOrder={columnOrder}
                          onColumnOrderChange={layout.handleColumnOrderChange}
                          noteColumnOrder={noteColumnOrder}
                          onNoteColumnOrderChange={layout.handleNoteColumnOrderChange}
                          stableColumnOrder={mode === "view" ? viewColumnOrder : undefined}
                          onStableColumnOrderChange={mode === "view" ? setViewColumnOrder : undefined}
                          columnFilters={columnFilters}
                          columnTypes={columnTypes}
                          onColumnFilterChange={handleColumnFilterChange}
                          onNoteColumnDeleted={handleNoteColumnDeleted}
                          columnWidths={columnWidths}
                          onColumnResize={handleColumnResize}
                          loadMoreRef={loadMoreRef}
                          tableScrollRef={tableScrollRef}
                          isLoadingMore={isLoadingMoreGridRows}
                          hasMoreRows={hasNextGridPage}
                          showLoadMoreStatus={totalGridRows > PAGE_SIZE}
                          onLoadMore={requestNextGridPage}
                          interactionDisabled={isGridResultStale}
                          readOnly={!gridCapabilities.editRows}
                          showSelectionColumn={gridCapabilities.bulkSelection}
                          showActionsColumn={!isCompactPreview}
                          onOpenOutcome={
                            isContractFamilyProjection ? outcomeId => handleOutcomeClick(outcomeId, []) : undefined
                          }
                        />
                      )}
                    </SnapshotQaSplitView>
                  </div>
                </div>
              </div>
            ) : (
              <div className="min-h-0 flex-grow overflow-auto">
                <React.Suspense fallback={<div className="p-6 text-sm text-gray-500">Loading pivot dashboard</div>}>
                  <PivotDashboard
                    savedPivots={dashboard.savedPivots}
                    onCreatePivot={() => dashboard.togglePivotBuilderOpen(true)}
                    onDeletePivot={dashboard.handleDeletePivot}
                    isLoading={dashboard.isLoadingSavedPivots}
                    isUnavailable={!dashboard.isPivotDataComplete}
                    unavailableMessage={dashboard.pivotUnavailableMessage}
                  />
                </React.Suspense>
              </div>
            )}
          </div>

          <FamilySourceDocumentsDialog
            collectionId={collectionId}
            familyId={expandedFamilyId}
            onClose={() => setExpandedFamilyId(null)}
          />

          <DeleteSelectedSnapshotsDialog
            open={bulkDelete.showDeleteDialog}
            onOpenChange={setShowDeleteDialog}
            count={selectedSnapshots.length}
            isSubcollection={Boolean(subcollectionId)}
            onConfirmDelete={bulkDelete.handleDeleteSelected}
            isDeleting={bulkDelete.isDeleting}
          />

          {columnAnalysisField && snapshotCompleted.length > 0 && (
            <SnapshotGridPortal
              isOpen
              setIsOpen={isOpen => {
                if (!isOpen) setColumnAnalysisField(null);
              }}
              businessReportOutcome={snapshotCompleted[0]}
              snapshotCollectionId={snapshotCollection.id}
              subcollectionId={subcollectionId}
              initialTab="columnAnalysis"
              columnName={columnAnalysisField}
              contractFamilyProjection={isContractFamilyProjection}
            />
          )}

          {detail.sidebarOutcome && (
            <SnapshotGridPortal
              isOpen
              setIsOpen={isOpen => {
                if (!isOpen) detail.closeSidebar();
              }}
              businessReportOutcome={detail.sidebarOutcome}
              result={detail.sidebarResult}
              snapshotCollectionId={snapshotCollection.id}
              subcollectionId={subcollectionId}
              canEditResult={gridCapabilities.editRows && canEditOutcomeResults(detail.sidebarOutcome)}
            />
          )}

          {hasDatePrompts && (
            <SnapshotDateFilterModal
              open={dateFilterState.showDateFilterModal}
              onOpenChange={dateFilterState.setShowDateFilterModal}
              startDate={dateFilter.startDate}
              endDate={dateFilter.endDate}
              selectedField={dateFilter.selectedField}
              availableFields={availableDateFields}
              onDateChange={dateFilterState.handleDateFilterChange}
              onClear={dateFilterState.handleDateFilterClear}
            />
          )}

          {isShareOpen && (
            <SnapshotCollectionShareModal
              isOpen={isShareOpen}
              setIsOpen={toggleIsShareOpen}
              snapshotCollection={snapshotCollection}
            />
          )}

          <ReviewRunConfirmDialog
            open={!!review.reviewDialogColumn}
            columnName={review.reviewDialogColumn?.header ?? ""}
            rowCount={totalGridRows}
            isSubmitting={review.isStartingReview}
            onConfirm={review.handleConfirmReviewRun}
            onOpenChange={open => !open && review.closeReviewDialog()}
          />

          <BusinessReportPromptEditModal
            key={aiColumn.isAddColumnOpen ? "add-column-open" : "add-column-closed"}
            prompt={EMPTY_PROMPT}
            isOpen={aiColumn.isAddColumnOpen}
            onClose={aiColumn.toggleAddColumnOpen}
            onSave={aiColumn.handleAddColumn}
            isLoading={aiColumn.isAddingColumn}
            templateType="snapshot_template"
            title="Add Column"
            submitLabel="Add column"
          />

          {dashboard.isPivotBuilderOpen && dashboard.isPivotDataComplete && (
            <React.Suspense fallback={null}>
              <PivotBuilderDialog
                open={dashboard.isPivotBuilderOpen}
                outcomes={snapshotCompleted}
                fieldSchema={dashboard.pivotFieldSchema}
                onOpenChange={dashboard.togglePivotBuilderOpen}
                onSavePivot={dashboard.handleSavePivot}
                normaliseField={dashboard.handleNormalisePivotField}
              />
            </React.Suspense>
          )}
        </div>
      </CurrentPdfProvider>
      <SaveSnapshotViewDialog
        open={showSaveViewDialog}
        onOpenChange={setShowSaveViewDialog}
        snapshotCollectionId={snapshotCollectionId}
        configuration={effectiveViewConfiguration}
        snapshotView={snapshotView}
      />
    </>
  );
};
